import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  buildTaxonomyIndex,
  type TaxonomyNode,
} from "../offeredServices/catalogueModel.js";
import {
  AUTO_FILLED_PROJECT_TYPES,
  OFFERED_PROJECT_TYPES,
} from "../offeredServices/projectTypes.js";

export type MatrixSelection = {
  categoryId: string;
  projectType: OfferedProjectType;
};

export type OnboardingFlowWizardState = {
  selectedVerticals: string[];
  selectedProjectTypes: OfferedProjectType[];
  // "yes": the chips apply to every service below the selected verticals;
  // "no": the services are customised - the user applied the matrix modal's
  // draft and the commit uses the matrix instead of the chips.
  applyToAll: "yes" | "no";
  // The applied matrix. A category appears in `matrixDeclaredCategories` iff
  // the user declared they offer it; this is independent of
  // `matrixSelections` (declared rows may have zero project-type cells set).
  // Both stay empty while the services are not customised.
  matrixDeclaredCategories: string[];
  matrixSelections: MatrixSelection[];
};

export const initialOnboardingFlowWizardState: OnboardingFlowWizardState = {
  selectedVerticals: [],
  // Preselected so the common case is one click: the auto-filled types
  // (everything except DIY) apply to all services of the chosen verticals.
  selectedProjectTypes: [...AUTO_FILLED_PROJECT_TYPES],
  applyToAll: "yes",
  matrixDeclaredCategories: [],
  matrixSelections: [],
};

// Structural shape of the generated `SuggestedOfferedServices` type.
export type SuggestedOfferedServicesInput = {
  categories: Array<string>;
  services: Array<MatrixSelection>;
};

const DELETED_ROOT_ID = "__deleted__";

const withoutDeletedSubtree = (
  categories: readonly TaxonomyNode[]
): TaxonomyNode[] => {
  const index = buildTaxonomyIndex(categories);
  return categories.filter(
    c =>
      c.id !== DELETED_ROOT_ID &&
      !index.ancestorsOf(c.id).includes(DELETED_ROOT_ID)
  );
};

// Wizard start state derived from an organisation's suggested offered services
// (MEIS-13566 design D13). `null`/`undefined` suggestions give the default
// state. Suggestions replace the default work-type chips: the chips are the
// suggested services' work types, none when the template has no services.
export const initialWizardStateFromSuggestions = (
  suggestions: SuggestedOfferedServicesInput | null | undefined,
  categories: readonly TaxonomyNode[]
): OnboardingFlowWizardState => {
  if (!suggestions) return initialOnboardingFlowWizardState;

  // The trades step and its matrix modal drop the "__deleted__" sentinel
  // subtree, so suggestions are mapped against the same usable taxonomy.
  const usable = withoutDeletedSubtree(categories);
  const index = buildTaxonomyIndex(usable);
  const suggested = [
    ...new Set(
      [
        ...suggestions.categories,
        ...suggestions.services
          .map(s => s.categoryId)
          .filter(id => index.isLeaf(id)),
      ].filter(id => index.has(id))
    ),
  ];
  // Services are only shown (and committed) on leaves: the matrix renders
  // cells for leaf rows only.
  const services = suggestions.services.filter(s => index.isLeaf(s.categoryId));

  const needed = new Set(suggested);
  for (const id of suggested) {
    for (const ancestorId of index.ancestorsOf(id)) needed.add(ancestorId);
  }
  const selectedVerticals = [
    ...new Set(
      usable
        .filter(c => (c.parentId ?? null) === null && needed.has(c.id))
        .map(c => c.id)
    ),
  ];

  const serviceProjectTypes = new Set(services.map(s => s.projectType));
  const selectedProjectTypes = OFFERED_PROJECT_TYPES.filter(pt =>
    serviceProjectTypes.has(pt)
  );

  // With services the wizard starts customised on exactly the template's
  // cells, so the commit reproduces them and no cross-product prefill runs.
  const hasServices = services.length > 0;
  return {
    selectedVerticals,
    selectedProjectTypes,
    applyToAll: hasServices ? "no" : "yes",
    matrixDeclaredCategories: hasServices ? suggested : [],
    matrixSelections: hasServices ? services : [],
  };
};

// Discriminated union of reducer actions. Names are imperative-mood; one case
// per action in onboardingFlowWizardReducer.
export type OnboardingFlowWizardAction =
  // Changing the trades discards a customised matrix.
  | { type: "toggleVertical"; categoryId: string }
  // While customised, the work type is also set (or cleared) on every declared
  // leaf of the matrix; `declaredLeafIds` names those leaves.
  | {
      type: "toggleProjectType";
      projectType: OfferedProjectType;
      declaredLeafIds: readonly string[];
    }
  // Apply in the matrix modal: stores the draft as the applied matrix and marks
  // the services as customised. `selections` may retain cells for undeclared
  // categories (session memory) - the commit selector filters those out.
  | {
      type: "applyMatrix";
      declaredCategories: string[];
      selections: MatrixSelection[];
    }
  // "Reset to work types": discards the matrix and returns to the chips.
  | { type: "resetCustomisation" }
  | { type: "reset" };

// Discriminant for which path the user took on the trades step to arrive at the
// setOrganisationOfferedServices mutation - drives the path -> payload mapping
// in selectOfferedServicesPayload. Skipping the trades step commits nothing, so
// it has no path here.
export type OfferedServicesCommitPath = "all-services" | "customised";

export type OfferedServicesPayload = {
  categories: string[];
  services: { categoryId: string; projectType: OfferedProjectType }[];
  // MEIS-13070: the terminal onboarding commit — and only it — asks the backend
  // to subscribe the organisation to the declared categories' and project
  // types' default templates. The My Organisation settings screen issues the
  // same mutation without this flag; onboarding holds no server-side state, so
  // it is the only signal that distinguishes the two callers.
  subscribeDefaultTemplates: boolean;
};
